import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Space,
  Table,
  message,
  DatePicker,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  fetchExchangeRates,
  createExchangeRate,
  updateExchangeRate,
  deleteExchangeRate,
  dispatchExchangeRatesUpdated,
  EXCHANGE_RATES_UPDATED_EVENT,
} from "../../../shared/lib/exchangeRates";
import { readCachedSystemConfig, fetchSystemConfig } from "../../../shared/lib/systemConfig";
import { toApiNumber } from "../../../utils/formatters";
import ListSearch from "../../../shared/ui/ListSearch";
import { filterBySearch } from "../../../shared/lib/listSearch";
import "../../../shared/styles/ErpPages.css";

const ExchangeRates = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const [systemConfig, setSystemConfig] = useState(() => readCachedSystemConfig());

  const mainCode = systemConfig?.currency_code || "IQD";
  const secondaryCode = systemConfig?.secondary_currency_code || "";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const cfg = await fetchSystemConfig();
      setSystemConfig(cfg);
      const list = await fetchExchangeRates();
      setRows(Array.isArray(list) ? list : []);
    } catch (e) {
      message.error(e?.response?.data?.message || t("exchangeRatesLoadFailed"));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onRefresh = () => load();
    window.addEventListener(EXCHANGE_RATES_UPDATED_EVENT, onRefresh);
    return () => window.removeEventListener(EXCHANGE_RATES_UPDATED_EVENT, onRefresh);
  }, [load]);

  const list = useMemo(
    () =>
      filterBySearch(rows, search, (r) => [
        r.id,
        r.effective_from,
        r.main_per_secondary,
        r.note,
      ]),
    [rows, search]
  );

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      effective_from: dayjs(),
      main_per_secondary: undefined,
      note: "",
    });
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      effective_from: record.effective_from ? dayjs(record.effective_from) : dayjs(),
      main_per_secondary: toApiNumber(record.main_per_secondary, 0),
      note: record.note || "",
    });
    setModalOpen(true);
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: t("exchangeRatesDeleteConfirm"),
      okType: "danger",
      okText: t("delete", "Delete"),
      cancelText: t("cancel", "Cancel"),
      onOk: async () => {
        try {
          await deleteExchangeRate(record.id);
          message.success(t("exchangeRatesDeleted"));
          dispatchExchangeRatesUpdated();
          load();
        } catch (e) {
          message.error(e?.response?.data?.message || t("exchangeRatesDeleteFailed"));
        }
      },
    });
  };

  const columns = useMemo(() => {
    const dash = t("exchangeRatesDash");
    return [
      {
        title: t("id", "ID"),
        dataIndex: "id",
        width: 70,
        sorter: (a, b) => a.id - b.id,
      },
      {
        title: t("exchangeRatesEffectiveFrom"),
        dataIndex: "effective_from",
        key: "effective_from",
        width: 140,
        sorter: (a, b) => String(a.effective_from || "").localeCompare(String(b.effective_from || "")),
        defaultSortOrder: "descend",
        render: (v) => (v ? dayjs(v).format("YYYY-MM-DD") : dash),
      },
      {
        title: t("exchangeRatesColumnRate", { main: mainCode, secondary: secondaryCode || "?" }),
        dataIndex: "main_per_secondary",
        key: "main_per_secondary",
        align: "right",
        render: (v) =>
          v !== undefined && v !== null ? (
            <span className="erp-money">{Number(v).toLocaleString()}</span>
          ) : (
            dash
          ),
      },
      {
        title: `100 ${mainCode || "?"} = ? ${secondaryCode || "?"}`,
        key: "per_100",
        align: "right",
        render: (_, record) => {
          const v = toApiNumber(record?.main_per_secondary, 0);
          if (!v) return dash;
          return (
            <span className="erp-money">
              {(v * 100).toLocaleString()} {mainCode}
            </span>
          );
        },
      },
      {
        title: t("exchangeRatesColumnInverse"),
        key: "inv",
        render: (_, record) => {
          const m = toApiNumber(record?.main_per_secondary, 0);
          if (!m) return dash;
          const inv = 1 / m;
          return t("exchangeRatesInverseApprox", {
            secondary: secondaryCode || "?",
            inv: inv.toLocaleString(undefined, { maximumFractionDigits: 6 }),
            main: mainCode,
          });
        },
      },
      {
        title: t("exchangeRatesColumnNote"),
        dataIndex: "note",
        key: "note",
        ellipsis: true,
        render: (v) => v || "—",
      },
      {
        title: t("action", "Action"),
        key: "actions",
        width: 180,
        fixed: "right",
        render: (_, record) => (
          <Space size={4} wrap>
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>
              {t("edit", "Edit")}
            </Button>
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
            >
              {t("delete", "Delete")}
            </Button>
          </Space>
        ),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainCode, secondaryCode, t]);

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        effective_from: values.effective_from.format("YYYY-MM-DD"),
        main_per_secondary: values.main_per_secondary,
        note: values.note || null,
      };
      setSaving(true);
      if (editing) {
        await updateExchangeRate(editing.id, payload);
        message.success(t("exchangeRatesUpdated"));
      } else {
        await createExchangeRate(payload);
        message.success(t("exchangeRatesAdded"));
      }
      setModalOpen(false);
      dispatchExchangeRatesUpdated();
      load();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || t("exchangeRatesSaveFailed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="erp-page">
      <div className="erp-shell">
        <div className="erp-header">
          <div>
            <div className="erp-breadcrumb">
              {t("sidebarAdministration", "Administration")} / {t("exchangeRatesPageTitle", "Exchange rates")}
            </div>
            <h1>{t("exchangeRatesPageTitle", "Exchange rates")}</h1>
          </div>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
              {t("refresh", "Refresh")}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreate}
              disabled={!secondaryCode}
            >
              {t("exchangeRatesAddRate", "Add rate")}
            </Button>
          </Space>
        </div>

        <div className="erp-meta">
          <span>
            {t("records", "Records")}: <b>{rows.length}</b>
          </span>
          <span>
            {t("settingsMainCurrencyCode", "Main")}: <b>{mainCode}</b>
          </span>
          <span>
            {t("settingsSecondaryCurrencyOptional", "Secondary")}:{" "}
            <b>{secondaryCode || "—"}</b>
          </span>
          {!secondaryCode ? (
            <span className="erp-money debt">
              {t("exchangeRatesNoSecondaryWarning", { main: mainCode })}{" "}
              <Link to="/settings/system">{t("systemSettings", "System settings")}</Link>
            </span>
          ) : (
            <span>
              {t("exchangeRatesHelp", { main: mainCode, secondary: secondaryCode })}
            </span>
          )}
        </div>

        <div className="erp-toolbar">
          <ListSearch
            value={search}
            onChange={setSearch}
            placeholder={t("searchPlaceholder")}
            width={280}
          />
        </div>

        <div className="erp-body">
          <Table
            size="small"
            rowKey="id"
            loading={loading}
            dataSource={list}
            columns={columns}
            pagination={{
              pageSize: 50,
              showSizeChanger: true,
              showTotal: (n) => `${n} ${t("records", "records")}`,
            }}
            scroll={{ x: "max-content" }}
            locale={{ emptyText: t("noDataFound", "No records") }}
          />
        </div>
      </div>

      <Modal
        title={editing ? t("exchangeRatesModalEdit") : t("exchangeRatesModalAdd")}
        open={modalOpen}
        onOk={handleModalOk}
        confirmLoading={saving}
        onCancel={() => setModalOpen(false)}
        okText={t("save", "Save")}
        cancelText={t("cancel", "Cancel")}
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="effective_from"
            label={t("exchangeRatesEffectiveFrom")}
            rules={[{ required: true }]}
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="main_per_secondary"
            label={t("exchangeRatesHowManyLabel", {
              secondary: secondaryCode || "?",
              main: mainCode,
            })}
            rules={[{ required: true, message: t("exchangeRatesRateRequired") }]}
          >
            <InputNumber min={0.000001} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="note" label={t("exchangeRatesNote")}>
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ExchangeRates;
